import { z } from 'zod';
import { modelSeatSchema } from './models.js';
import {
  expectedNoyauReadingSchema,
  noyauGridScoreSchema,
  npcExpectedNoyauSchema,
} from './noyau-expected.js';
import { NOYAU_CODES, noyauCardIdSchema, noyauCodeSchema } from './noyau.js';
import { replayModeSchema, replayPlayerSchema } from './replay-mark.js';
import { REPLAY_SOURCE_TEXT_MAX, npcRefusalCodeSchema, replayRefusalCodeSchema } from './replay.js';
import {
  sessionRefusalCodeSchema,
  sessionValuesSchema,
  sessionViewSchema,
} from './session-play.js';
import { sessionStatusSchema } from './session.js';
import { SESSION_MESSAGE_MAX_CHARS } from './turn.js';
import {
  VERSIONED_DEFAULT_SORT,
  renameRequestSchemaFor,
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionTagSchema,
  versionedCardShape,
  versionedContentSchema,
  versionedDetailSchema,
  versionedSortSchema,
} from './versioning.js';

/**
 * An NPC: the PERSON a replay plays (docs/specs/npc.md, issue #210).
 *
 * Fifth consumer of the common versioning pattern (refonte modulaire §4), and
 * the first with ONE side only: development. Everything generic - the versions,
 * the draft, the retirement, the history - comes from `versioning.ts`, exactly
 * as for the persona; what is here depends on what an NPC MEANS. The spec says
 * who is what: a persona gives its voice to the INTERVIEWER, an NPC plays the
 * person (§2).
 *
 * ## Two names, again
 *
 * `name` is the OPERATOR's name (« Marie Curie, le hangar et le radium »), unique
 * among the non-retired NPC. `firstName` is what the player types into the
 * pages (« Marie »).
 *
 * ## What the player does NOT receive
 *
 * The kind, the labels, the moment and the expected noyau are for the operator
 * (§3.3): the player must not know which noyau is expected of it. They are in
 * the content and NOT in `composeNpcBlock`, which is the one function that
 * decides what travels.
 *
 * ## Why this file imports `replay.ts` and never the other way
 *
 * `replay.ts` carries the replay start and the refusals, which NAME an NPC and
 * a series by a bare identifier; none of those needs a shape from here. The
 * arrow therefore goes one way only, and a cycle of ESM modules reads a schema
 * before it exists.
 */

/* --------------------------------------------------------------- the bounds */

/**
 * H27 (§3.1). Bounds of INPUT, not of meaning: an operator meets them by
 * mistake, never on purpose.
 */
export const NPC_NAME_MAX = 120;
export const NPC_FIRST_NAME_MAX = 60;
export const NPC_WHO_MAX = 300;
export const NPC_HOW_TO_PLAY_MAX = 4000;
export const NPC_MOMENT_MAX = 300;
/** H7 (§3.2): the title of a source, its address, its note of provenance. */
export const NPC_SOURCE_TITLE_MAX = 120;
export const NPC_SOURCE_URL_MAX = 2000;
export const NPC_SOURCE_PROVENANCE_MAX = 2000;
/**
 * H7: ALL the texts of an NPC together, the bound of the source of a replay
 * (`rejeu.md` §5). A ceiling of the SUM, which no field-level bound can say.
 */
export const NPC_SOURCES_TEXT_MAX = REPLAY_SOURCE_TEXT_MAX;
/**
 * A ceiling of PLAUSIBILITY on how many sources one NPC carries. The spec bounds
 * the characters and not the count, so without it a request could carry a
 * hundred thousand one-character sources under the total.
 */
export const NPC_SOURCES_MAX = 50;
/** The name of a bench (§5): the bound of an NPC's name. */
export const NPC_BENCH_NAME_MAX = NPC_NAME_MAX;
/** A ceiling of plausibility on the NPC of one bench: the bank of #204 holds fifteen. */
export const NPC_BENCH_NPCS_MAX = 100;
/** Passes of one launch (H31). */
export const NPC_PASSES_MIN = 1;
export const NPC_PASSES_MAX = 5;
/** The reason an interrupted or blocked run carries, and the detail of a refused call. */
export const NPC_REASON_MAX = 600;
/** The label a seeded series carries (« jouée avant les NPC »). */
export const NPC_SERIES_LABEL_MAX = 120;

/* ------------------------------------------------------------ the enumerations */

/**
 * What an NPC IS (§3.1): a famous person of the public domain, a profile
 * written by Claude, or a real session an operator turned into an NPC (§4.2).
 * The colour of the avatar says it.
 */
export const npcKindSchema = z.enum(['famous', 'written', 'session']);
export type NpcKind = z.infer<typeof npcKindSchema>;

/** The French names of the three kinds, as the cards and the filters write them. */
export const NPC_KIND_LABELS: Readonly<Record<NpcKind, string>> = {
  famous: 'Célèbre',
  written: 'Profil écrit',
  session: 'Session réelle',
};

/**
 * What an NPC « éprouve » (H5): the closed list of the eight difficult
 * behaviours of the bench (`scenario-succes-conduite.md` §15.2). One more label
 * is one more line here and in the table below.
 */
export const npcBehaviourSchema = z.enum([
  'generalist',
  'example-first',
  'short-answers',
  'loses-interest',
  'wrong-subject',
  'no-passion',
  'questions-interviewer',
  'informal-address',
]);
export type NpcBehaviour = z.infer<typeof npcBehaviourSchema>;
export const NPC_BEHAVIOURS = npcBehaviourSchema.options;

export const NPC_BEHAVIOUR_LABELS: Readonly<Record<NpcBehaviour, string>> = {
  generalist: 'La généraliste',
  'example-first': "L'exemple d'emblée",
  'short-answers': 'Les réponses courtes',
  'loses-interest': 'Celle qui se lasse',
  'wrong-subject': 'Le mauvais sujet',
  'no-passion': 'Pas de passion',
  'questions-interviewer': "Celle qui questionne l'interviewer",
  'informal-address': 'Le tutoiement',
};

/*
 * The expected noyau (H6): `OUT_OF_BASE` and `npcExpectedNoyauSchema` now live in
 * `noyau-expected.ts` (issue #216), beside the verdict that compares it to the
 * found noyau, because `replay-mark.ts` carries the response shapes and cannot
 * import this file. The index exports them from there, under the same names.
 */

/* -------------------------------------------------------------- the sources */

/**
 * One source of an NPC (§3.2, H7): a title, an address that is NEVER read by the
 * server, the text pasted whole, and the note of where it comes from.
 *
 * NOT `.strict()`, like every stored shape (§4.1). The text is kept as pasted:
 * it is the person's own words, and « mot pour mot » is the rule of the bench.
 * It must not be BLANK, which a refinement says, because trimming it would
 * alter what was pasted.
 */
export const npcSourceSchema = z.object({
  title: z.string().trim().min(1).max(NPC_SOURCE_TITLE_MAX),
  /** Null when the source has none: the block then prints `Source : <title>`. */
  url: z.string().trim().min(1).max(NPC_SOURCE_URL_MAX).nullable().default(null),
  text: z
    .string()
    .min(1)
    .max(NPC_SOURCES_TEXT_MAX)
    .refine((text) => text.trim().length > 0, { message: 'A source text is not blank.' }),
  /** Translation, edition, what was repaired. May be empty. */
  provenance: z.string().trim().max(NPC_SOURCE_PROVENANCE_MAX).default(''),
});
export type NpcSource = z.infer<typeof npcSourceSchema>;

/** The characters of every text of an NPC together: what H7 bounds. */
export function npcSourcesChars(sources: readonly Pick<NpcSource, 'text'>[]): number {
  return sources.reduce((total, source) => total + source.text.length, 0);
}

/**
 * The business refusal of sources the field-level bounds let through (§17): the
 * total past 100 000. Null when they fit. PURE, at the régime of
 * `replaySourceProblem`, so the route and the tool word the same refusal.
 */
export function npcSourcesProblem(
  sources: readonly Pick<NpcSource, 'text'>[],
): 'source-too-long' | null {
  return npcSourcesChars(sources) > NPC_SOURCES_TEXT_MAX ? 'source-too-long' : null;
}

/** The issue both content schemas raise; the code a screen words is its message. */
function sourcesTotalIssue() {
  return {
    code: z.ZodIssueCode.custom,
    path: ['sources'],
    message: 'source-too-long',
  };
}

/* -------------------------------------------------------------- the content */

/**
 * The content as a plain OBJECT, which `.partial()` and `.shape` need - the
 * refinement of the sources' total is added on top of it below, and a refined
 * schema is neither.
 *
 * `behaviours` carries no duplicate (a label is on or off), and `moment` may be
 * empty: it bottoms out at 0 and defaults to the empty string, at the régime of
 * the persona's `background`. A field added later arrives `optional` with a
 * default at read time (§4.1).
 */
export const npcContentObjectSchema = z.object({
  name: z.string().trim().min(1).max(NPC_NAME_MAX),
  firstName: z.string().trim().min(1).max(NPC_FIRST_NAME_MAX),
  kind: npcKindSchema,
  who: z.string().trim().min(1).max(NPC_WHO_MAX),
  howToPlay: z.string().trim().min(1).max(NPC_HOW_TO_PLAY_MAX),
  sources: z.array(npcSourceSchema).min(1).max(NPC_SOURCES_MAX),
  behaviours: z
    .array(npcBehaviourSchema)
    .max(NPC_BEHAVIOURS.length)
    .refine((labels) => new Set(labels).size === labels.length, {
      message: 'A behaviour is listed once.',
    })
    .default([]),
  moment: z.string().trim().max(NPC_MOMENT_MAX).default(''),
  expectedNoyau: npcExpectedNoyauSchema.nullable().default(null),
  /**
   * The session an NPC was born from (§4.2), null on a written one. A bare
   * identifier WITHOUT a foreign key: the NPC outlives its session (N14), and a
   * link to a purged one reads « session supprimée ».
   */
  originSessionId: z.string().min(1).nullable().default(null),
});

/**
 * The CONTENT of one version - the totality of the NPC at that version, name
 * included (§4.1). Deliberately not `.strict()`: it parses rows written by
 * earlier versions of the code.
 *
 * The total of the texts (H7) is a refinement, so that a version over 100 000
 * characters is refused wherever it comes from; the wording a screen shows
 * comes from the code `source-too-long`.
 */
export const npcContentSchema = npcContentObjectSchema.superRefine((content, context) => {
  if (npcSourcesProblem(content.sources) !== null) context.addIssue(sourcesTotalIssue());
});
export type NpcContent = z.infer<typeof npcContentSchema>;

/**
 * The content of a DRAFT: the same fields, each optional - an empty field is
 * spelled by the key being ABSENT, so a half-written NPC survives a reload.
 * The total of the texts holds for a draft too, because a draft over it could
 * never become a version.
 */
export const npcDraftContentSchema = npcContentObjectSchema
  .partial()
  .superRefine((content, context) => {
    if (content.sources !== undefined && npcSourcesProblem(content.sources) !== null) {
      context.addIssue(sourcesTotalIssue());
    }
  });
export type NpcDraftContent = z.infer<typeof npcDraftContentSchema>;

/* ------------------------------------------------------- what the player gets */

/** What `composeNpcBlock` reads: the four fields that travel, and nothing else. */
export type NpcBlockInput = Pick<NpcContent, 'who' | 'firstName' | 'howToPlay'> & {
  sources: readonly Pick<NpcSource, 'title' | 'url' | 'text'>[];
};

/**
 * The block of the NPC as the player receives it (§3.3, H8): PURE, in the form
 * of the files of the bench.
 *
 * ```
 * <who>
 * Prénom : <first name>.
 *
 * Comment jouer : <how to play>
 *
 * Source : <title> (<address>)
 *
 * <text of source 1>
 *
 * Source : <title>
 *
 * <text of source 2>
 * ```
 *
 * A source without an address prints `Source : <title>` with no parentheses.
 * The texts go out AS THEY ARE STORED: the words are the person's, and the block
 * is frozen on the replay whole, with the version it played.
 *
 * What is NOT here is the whole point: the kind, the labels, the moment, the
 * expected noyau and the notes of provenance stay with the operator, because the
 * player must not know which noyau is expected of it.
 */
export function composeNpcBlock(content: NpcBlockInput): string {
  const parts = [
    `${content.who}\nPrénom : ${content.firstName}.`,
    `Comment jouer : ${content.howToPlay}`,
  ];
  for (const source of content.sources) {
    parts.push(
      source.url === null ? `Source : ${source.title}` : `Source : ${source.title} (${source.url})`,
    );
    parts.push(source.text);
  }
  return parts.join('\n\n');
}

/* ---------------------------------------------------------------- the cards */

/**
 * One card of the list (§12): the thirteen common fields of
 * `versionedCardShape`, plus the facets that belong to an NPC. All are read off
 * the DEVELOPMENT version - an NPC has no other.
 *
 * `usage` stays in the common shape and stays EMPTY: an NPC is cited by nothing
 * (§3.4); what it says instead is `replayCount`, « joué par N rejeux » - the
 * replays that played this entity, any of its versions.
 */
export const npcCardSchema = z
  .object({
    ...versionedCardShape,
    kind: npcKindSchema,
    firstName: z.string().min(1),
    sourceCount: z.number().int().nonnegative(),
    behaviours: z.array(npcBehaviourSchema),
    moment: z.string(),
    expectedNoyau: npcExpectedNoyauSchema.nullable(),
    replayCount: z.number().int().nonnegative(),
  })
  .strict();
export type NpcCard = z.infer<typeof npcCardSchema>;

/** The list, served WHOLE - retired entries included - and filtered in the page. */
export const npcListSchema = z.object({ npcs: z.array(npcCardSchema) }).strict();
export type NpcList = z.infer<typeof npcListSchema>;

/** How many replays played one version: the history says « joué par N rejeux » (§3.4). */
export const npcVersionReplayCountSchema = z
  .object({ versionId: z.string().min(1), count: z.number().int().positive() })
  .strict();
export type NpcVersionReplayCount = z.infer<typeof npcVersionReplayCountSchema>;

/**
 * Everything the editor reads in one call: the common detail, plus the two
 * counts that replace « utilisé dans » (§3.4) - the replays of the entity, and
 * of each version that has any.
 */
export const npcDetailSchema = versionedDetailSchema(
  npcContentSchema,
  npcDraftContentSchema,
).extend({
  replayCount: z.number().int().nonnegative(),
  versionReplayCounts: z.array(npcVersionReplayCountSchema),
});
export type NpcDetail = z.infer<typeof npcDetailSchema>;

/** One version read in full, for « Voir ». */
export const npcVersionSchema = versionedContentSchema(npcContentSchema);
export type NpcVersion = z.infer<typeof npcVersionSchema>;

/**
 * « Enregistrer une nouvelle version ». `alsoProduction` is part of the common
 * body and is refused by the route: an NPC has no production side
 * (`refuseProduction`, §3.4).
 */
export const npcSaveVersionSchema = saveVersionRequestSchema(npcContentSchema);
export type NpcSaveVersion = z.infer<typeof npcSaveVersionSchema>;

/** An autosave of the draft. */
export const npcSaveDraftSchema = saveDraftRequestSchema(npcDraftContentSchema);
export type NpcSaveDraft = z.infer<typeof npcSaveDraftSchema>;

/**
 * Renaming, on the NPC's OWN name field, for the reason the persona's does: the
 * rename writes into the draft that `npcDraftContentSchema` reads back, and two
 * bounds that could diverge would commit a name no later read can parse.
 */
export const npcRenameSchema = renameRequestSchemaFor(npcContentObjectSchema.shape.name);
export type NpcRename = z.infer<typeof npcRenameSchema>;

/** The common four sorts, and no more: an NPC adds none of its own. */
export const npcSortSchema = versionedSortSchema;
export type NpcSort = z.infer<typeof npcSortSchema>;
export const NPC_DEFAULT_SORT: NpcSort = VERSIONED_DEFAULT_SORT;

/**
 * `GET /api/npcs/prefill?session={id}` (§4.2, H18): the draft « Créer un NPC »
 * opens, or the refusal. The draft is the PARTIAL content (the operator reads it
 * and saves it as v1), and `sessionId` is the session it came from.
 */
export const npcPrefillSchema = z
  .object({
    sessionId: z.string().min(1),
    content: npcDraftContentSchema,
  })
  .strict();
export type NpcPrefill = z.infer<typeof npcPrefillSchema>;

/** Why a session cannot give an NPC (`prefill-refused`, §17): the line the greyed gesture shows. */
export const npcPrefillReasonSchema = z.enum([
  'replay',
  'in-progress',
  'no-protocol-page',
  'no-turn',
]);
export type NpcPrefillReason = z.infer<typeof npcPrefillReasonSchema>;

/** The French line each reason is shown with (§4.2). */
export const NPC_PREFILL_REASON_LABELS: Readonly<Record<NpcPrefillReason, string>> = {
  replay: 'Un rejeu ne devient pas un NPC',
  'in-progress': 'La session est en cours',
  'no-protocol-page': "La session n'a pas de page protocole",
  'no-turn': "La session n'a aucun tour",
};

/* ------------------------------------------------------------- the benches */

/**
 * The fields a bench carries, whatever gesture writes them (§5): a unique name
 * and an ORDERED list of NPC. NOT versioned (H4): a series freezes the list and
 * the version of each NPC at its launch, so touching a bench changes no series
 * already played.
 *
 * The list may be empty - an operator builds a bench by adding NPC - and a bench
 * with nothing to play is refused at the LAUNCH (`bench-empty`), not here.
 */
export const npcBenchWriteSchema = z
  .object({
    name: z.string().trim().min(1).max(NPC_BENCH_NAME_MAX),
    npcIds: z
      .array(z.string().min(1))
      .max(NPC_BENCH_NPCS_MAX)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'An NPC is listed once in a bench.',
      }),
  })
  .strict();
export type NpcBenchWrite = z.infer<typeof npcBenchWriteSchema>;

/** One NPC of a bench as the row shows it: the stack of initials, retired ones included (H24). */
export const npcBenchMemberSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    firstName: z.string().min(1),
    kind: npcKindSchema,
    /** Retired NPC stay in the bench and are skipped by a launch, which says so. */
    retired: z.boolean(),
  })
  .strict();
export type NpcBenchMember = z.infer<typeof npcBenchMemberSchema>;

/** One bench of the list (§5): « lancé N fois », the date of its last series. */
export const npcBenchSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    npcs: z.array(npcBenchMemberSchema),
    launchCount: z.number().int().nonnegative(),
    lastSeriesAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
    updatedAt: z.string().datetime(),
    updatedBy: z.string().min(1),
    retiredAt: z.string().datetime().nullable(),
  })
  .strict();
export type NpcBench = z.infer<typeof npcBenchSchema>;

export const npcBenchListSchema = z.object({ benches: z.array(npcBenchSchema) }).strict();
export type NpcBenchList = z.infer<typeof npcBenchListSchema>;

/**
 * `GET /api/npcs/{id}/referents` (§12): the benches that CARRY the NPC - what the
 * fiche lists under « Les bancs qui le portent », and what the box of the
 * definitive deletion names. An NPC is cited by nothing else (§3.4), and a bench is
 * not versioned, so this is NOT the plan of the save box (`referentPlanSchema`):
 * there is no version to update. Retired benches are listed, with their date.
 */
export const npcReferentsSchema = z
  .object({
    benches: z.array(
      z
        .object({
          id: z.string().min(1),
          name: z.string().min(1),
          retiredAt: z.string().datetime().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type NpcReferents = z.infer<typeof npcReferentsSchema>;

/**
 * `DELETE /api/npcs/{id}` (§3.5, N15, H25): the definitive deletion's answer - the
 * replays it sent to the trash (all of them, the ones it interrupted included) and
 * the benches that lost the NPC, retired ones too.
 */
export const npcDeletedSchema = z
  .object({
    trashedReplays: z.number().int().nonnegative(),
    leftBenches: z.array(z.object({ id: z.string().min(1), name: z.string().min(1) }).strict()),
  })
  .strict();
export type NpcDeleted = z.infer<typeof npcDeletedSchema>;

/* ---------------------------------------------------------- the launch input */

/** What a launch targets (§15): one NPC, or a bench. */
export const npcLaunchTargetSchema = z.union([
  z.object({ npcId: z.string().min(1) }).strict(),
  z.object({ benchId: z.string().min(1) }).strict(),
]);
export type NpcLaunchTarget = z.infer<typeof npcLaunchTargetSchema>;

/**
 * Who plays (N2, §6.1): the SERVER - paid, with the model of Réglages > Rejeu
 * unless this launch replaces it (N7), and `model` has the shape of one seat of
 * Réglages > Modèles (H28), pricing included - or CLAUDE, prepared by the app
 * for a conversation with the connector (§8).
 */
export const npcLaunchPlayerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('server'), model: modelSeatSchema.optional() }).strict(),
  z.object({ kind: z.literal('claude') }).strict(),
]);
export type NpcLaunchPlayer = z.infer<typeof npcLaunchPlayerSchema>;

/** The default of the box (§6.1): played by the server. */
export const NPC_DEFAULT_PLAYER: NpcLaunchPlayer = { kind: 'server' };

/**
 * `POST /api/npc-series` and `POST /api/npc-series/estimate` (§15): what a
 * launch takes. Every field of the box carries its default (§6.1), so the
 * assistant's tool can say less: development, strict, one pass, played by the
 * server.
 *
 * `passes` is bounded here (H31) AND by `npcPassesProblem`, which a caller that
 * checks before parsing words as `passes-out-of-range`, at the régime of
 * `replaySourceProblem`. `modelsVersionId` is a version of Réglages > Modèles
 * from the history (N8); absent, the models in force on the side chosen.
 */
export const npcLaunchSchema = z
  .object({
    target: npcLaunchTargetSchema,
    experienceId: z.string().min(1),
    tag: versionTagSchema.default('development'),
    mode: replayModeSchema.default('strict'),
    passes: z.number().int().min(NPC_PASSES_MIN).max(NPC_PASSES_MAX).default(NPC_PASSES_MIN),
    player: npcLaunchPlayerSchema.default(NPC_DEFAULT_PLAYER),
    modelsVersionId: z.string().min(1).optional(),
  })
  .strict();
export type NpcLaunch = z.infer<typeof npcLaunchSchema>;

/** The business refusal of passes outside 1 to 5 (§17), or null. PURE. */
export function npcPassesProblem(passes: number): 'passes-out-of-range' | null {
  return Number.isInteger(passes) && passes >= NPC_PASSES_MIN && passes <= NPC_PASSES_MAX
    ? null
    : 'passes-out-of-range';
}

/* ----------------------------------------------------------- the estimate */

/**
 * How one line of the estimate was made (H14): the cost of the LAST finished
 * replay of this NPC on this experience, else the MEDIAN cost of the finished
 * replays of the experience, else nothing - « estimation impossible ».
 */
export const npcEstimateMethodSchema = z.enum(['last-run', 'median', 'impossible']);
export type NpcEstimateMethod = z.infer<typeof npcEstimateMethodSchema>;

/** One NPC of the estimate: how many replays it will play, and what they should cost. */
export const npcEstimateLineSchema = z
  .object({
    npcId: z.string().min(1),
    name: z.string().min(1),
    /** The version in force at the launch, the one the series would freeze. */
    versionNumber: z.number().int().positive(),
    replays: z.number().int().positive(),
    method: npcEstimateMethodSchema,
    /** For all the NPC's replays, the device side only; null when `method` is `impossible`. */
    dispositifEur: z.number().nonnegative().nullable(),
  })
  .strict();
export type NpcEstimateLine = z.infer<typeof npcEstimateLineSchema>;

/**
 * The estimate of a launch (§6.2, H14, N19), read BEFORE leaving: it is NOT a
 * ceiling - nothing bounds the spend of a series.
 *
 * `dispositifEur` and `playerEur` are told apart (H15). The player prepared for
 * Claude costs nothing on the player's side: `playerEur` is 0 then, and null
 * when the server plays and nothing allows to say. `impossible` is true when NO
 * line can be estimated; a partial one lists its lines with `method:
 * 'impossible'`, counts them in `unestimatedReplays`, and totals the others.
 * `method` is the summary of the lines: the one they all share, or `mixed`.
 * `skipped` is the retired NPC of a bench, which the launch does not play (H24).
 */
export const npcEstimateSchema = z
  .object({
    replays: z.number().int().nonnegative(),
    unestimatedReplays: z.number().int().nonnegative(),
    dispositifEur: z.number().nonnegative().nullable(),
    playerEur: z.number().nonnegative().nullable(),
    method: z.union([npcEstimateMethodSchema, z.literal('mixed')]),
    impossible: z.boolean(),
    lines: z.array(npcEstimateLineSchema),
    skipped: z.array(z.object({ npcId: z.string().min(1), name: z.string().min(1) }).strict()),
  })
  .strict();
export type NpcEstimate = z.infer<typeof npcEstimateSchema>;

/* ----------------------------------------------------- the series and its runs */

/**
 * What a slot of a series stands in (§15, `npc_series_runs`): `waiting` - its
 * turn has not come, or Claude has not taken it -, `running`, `done`.
 */
export const npcRunStateSchema = z.enum(['waiting', 'running', 'done']);
export type NpcRunState = z.infer<typeof npcRunStateSchema>;

/**
 * How a replay of a series ended (§7.2, H11): finished - the last page was left;
 * closed - a fallback or a guardrail closed the session; departed - the player
 * chose to leave and the session ran on to its expiry (H10); blocked - a
 * malformed output asked twice, or ten minutes without action (H9, H30);
 * interrupted - a stop, a restart, a gesture (§7.3).
 */
export const npcRunOutcomeSchema = z.enum([
  'finished',
  'closed',
  'departed',
  'blocked',
  'interrupted',
]);
export type NpcRunOutcome = z.infer<typeof npcRunOutcomeSchema>;

/** The French word of each outcome, as the rows write it. */
export const NPC_RUN_OUTCOME_LABELS: Readonly<Record<NpcRunOutcome, string>> = {
  finished: 'finie',
  closed: 'fermée',
  departed: 'départ',
  blocked: 'bloquée',
  interrupted: 'interrompue',
};

/**
 * The series as a whole (§9): `running` (« en cours 5/16 »), `waiting-claude` (a
 * series prepared for Claude whose slots are not all taken), `finished`,
 * `stopped` (« Arrêter la série ») or `interrupted` (a restart cut it and nobody
 * relaunched). Derived by the server from the slots, never stored.
 */
export const npcSeriesStateSchema = z.enum([
  'running',
  'waiting-claude',
  'finished',
  'stopped',
  'interrupted',
]);
export type NpcSeriesState = z.infer<typeof npcSeriesStateSchema>;

/**
 * Who played a series, as it froze it: the server with the seat it used - route,
 * model, endpoint and pricing, so that a cost is readable after the settings
 * moved - or Claude.
 */
export const npcSeriesPlayerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('server'), model: modelSeatSchema }).strict(),
  z.object({ kind: z.literal('claude') }).strict(),
]);
export type NpcSeriesPlayer = z.infer<typeof npcSeriesPlayerSchema>;

/**
 * What a row of replays says (§10), read off the session and never computed
 * here: date, experience and version, side, mode, player, status, steps and
 * objectives, retained noyau, turns, the two costs. The line of a NPC's tab
 * « Rejeux » and the line of a series' table are the same row. Since #216 it
 * also carries the expected noyau of the version played, against the retained
 * one (`expected`, npc.md §26.8): the one comparison a screen makes, between a
 * session and the card of its NPC - never between two experiences (N10, A10).
 *
 * Every figure the session may not hold yet is null: a replay that has not left
 * its first page has no step reached.
 */
export const npcReplayRowSchema = z
  .object({
    sessionId: z.string().min(1),
    startedAt: z.string().datetime(),
    npcVersionNumber: z.number().int().positive().nullable(),
    seriesId: z.string().min(1).nullable(),
    seriesLabel: z.string().nullable(),
    pass: z.number().int().positive().nullable(),
    experienceId: z.string().min(1),
    experienceName: z.string().min(1),
    experienceVersionNumber: z.number().int().positive().nullable(),
    side: versionTagSchema,
    mode: replayModeSchema,
    player: replayPlayerSchema,
    status: sessionStatusSchema,
    outcome: npcRunOutcomeSchema.nullable(),
    stepsReached: z.number().int().nonnegative().nullable(),
    stepsTotal: z.number().int().nonnegative().nullable(),
    objectivesValidated: z.number().int().nonnegative().nullable(),
    objectivesTotal: z.number().int().nonnegative().nullable(),
    noyau: noyauCodeSchema.nullable(),
    /**
     * The expected noyau of the NPC version this replay played, against the
     * retained one (npc.md §26.8, issue #216): the expected noyau, its provenance,
     * the verdict, where it falls. Computed at the reading, never stored; null on
     * a replay with no NPC expected noyau at all (an out-of-base one is carried,
     * without a verdict), absent on a row built before #216.
     */
    expected: expectedNoyauReadingSchema.nullable().optional(),
    /**
     * The six notes of the determination, in the order of the table, for the grid
     * of the tab « Rejeux » (§26.4); null without a rendered determination, absent
     * on a row built before #216.
     */
    noyauScores: z.array(noyauGridScoreSchema).length(NOYAU_CODES.length).nullable().optional(),
    turns: z.number().int().nonnegative(),
    /** The device's cost - the column Coût of Sessions (H15). */
    costEur: z.number().nonnegative(),
    playerCostEur: z.number().nonnegative(),
  })
  .strict();
export type NpcReplayRow = z.infer<typeof npcReplayRowSchema>;

/** `GET /api/npcs/{id}/replays` (§10): the tab « Rejeux (12) », most recent first. */
export const npcReplayListSchema = z.object({ replays: z.array(npcReplayRowSchema) }).strict();
export type NpcReplayList = z.infer<typeof npcReplayListSchema>;

/** Where a running slot stands: « tour 7 · étape 3 ». */
export const npcRunProgressSchema = z
  .object({
    turn: z.number().int().nonnegative(),
    stepName: z.string().nullable(),
  })
  .strict();
export type NpcRunProgress = z.infer<typeof npcRunProgressSchema>;

/**
 * One SLOT of a series (§15): one per NPC and per pass, holding the version of
 * the NPC the launch froze. The slot outlives its session: a purge leaves it
 * with `sessionId` null (« session supprimée »), and an NPC deleted for good
 * leaves its name here.
 *
 * - `relaunchOf` - the slot this one replays, when « Relancer les rejeux
 *   interrompus » made it (§7.3); the interrupted one stays, and carries
 *   `relaunchedAt`;
 * - `rank` - the place of a `waiting` slot in the queue of the eight (H12), null
 *   once it left it or on a series prepared for Claude;
 * - `progress` - what a `running` slot says, null otherwise;
 * - `replay` - what its session recorded, null while there is no session.
 */
export const npcSeriesRunSchema = z
  .object({
    id: z.string().min(1),
    seriesId: z.string().min(1),
    npcId: z.string().min(1),
    npcName: z.string().min(1),
    npcVersionId: z.string().min(1),
    npcVersionNumber: z.number().int().positive(),
    pass: z.number().int().positive(),
    state: npcRunStateSchema,
    outcome: npcRunOutcomeSchema.nullable(),
    reason: z.string().max(NPC_REASON_MAX).nullable(),
    relaunchOf: z.string().min(1).nullable(),
    relaunchedAt: z.string().datetime().nullable(),
    sessionId: z.string().min(1).nullable(),
    startedAt: z.string().datetime().nullable(),
    endedAt: z.string().datetime().nullable(),
    rank: z.number().int().positive().nullable(),
    progress: npcRunProgressSchema.nullable(),
    replay: npcReplayRowSchema.nullable(),
  })
  .strict();
export type NpcSeriesRun = z.infer<typeof npcSeriesRunSchema>;

/** What a series was launched on: a bench (its name frozen), or one NPC. */
export const npcSeriesTargetSchema = z.discriminatedUnion('kind', [
  z
    .object({ kind: z.literal('bench'), benchId: z.string().min(1), benchName: z.string().min(1) })
    .strict(),
  z
    .object({ kind: z.literal('npc'), npcId: z.string().min(1), npcName: z.string().min(1) })
    .strict(),
]);
export type NpcSeriesTarget = z.infer<typeof npcSeriesTargetSchema>;

/**
 * One line of the tab « Séries » (§9), most recent first: the date, the bench
 * or the NPC, the experience and its version, the side, the mode, the player,
 * the state, the replays finished, the two costs.
 *
 * `label` is the label of a SEEDED series (« jouée avant les NPC », lot G), null
 * on a launched one. `experienceVersionNumber` is the version the tag resolved
 * to at the launch; each replay still freezes its own cascade.
 */
export const npcSeriesRowSchema = z
  .object({
    id: z.string().min(1),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
    label: z.string().max(NPC_SERIES_LABEL_MAX).nullable(),
    target: npcSeriesTargetSchema,
    experienceId: z.string().min(1),
    experienceName: z.string().min(1),
    experienceVersionNumber: z.number().int().positive().nullable(),
    tag: versionTagSchema,
    mode: replayModeSchema,
    passes: z.number().int().min(NPC_PASSES_MIN).max(NPC_PASSES_MAX),
    player: npcSeriesPlayerSchema,
    modelsVersionId: z.string().min(1).nullable(),
    state: npcSeriesStateSchema,
    slotCount: z.number().int().nonnegative(),
    runningCount: z.number().int().nonnegative(),
    finishedCount: z.number().int().nonnegative(),
    costEur: z.number().nonnegative(),
    playerCostEur: z.number().nonnegative(),
    stoppedAt: z.string().datetime().nullable(),
  })
  .strict();
export type NpcSeriesRow = z.infer<typeof npcSeriesRowSchema>;

export const npcSeriesListSchema = z.object({ series: z.array(npcSeriesRowSchema) }).strict();
export type NpcSeriesList = z.infer<typeof npcSeriesListSchema>;

/**
 * The banner of a series a restart cut (§7.3): « Le serveur a redémarré à 14:32 :
 * 4 rejeux interrompus ».
 */
export const npcSeriesRestartSchema = z
  .object({
    at: z.string().datetime(),
    interruptedCount: z.number().int().positive(),
  })
  .strict();
export type NpcSeriesRestart = z.infer<typeof npcSeriesRestartSchema>;

/**
 * `GET /api/npc-series/{id}` (§9): the row, its slots grouped by NPC and by pass,
 * the estimate made at the launch (null on a seeded series), the banner of a
 * restart, who stopped it, and - for a series prepared for Claude - the phrase to
 * paste (§8).
 */
export const npcSeriesDetailSchema = npcSeriesRowSchema
  .extend({
    stoppedBy: z.string().min(1).nullable(),
    estimate: npcEstimateSchema.nullable(),
    restart: npcSeriesRestartSchema.nullable(),
    claudePrompt: z.string().min(1).nullable(),
    runs: z.array(npcSeriesRunSchema),
  })
  .strict();
export type NpcSeriesDetail = z.infer<typeof npcSeriesDetailSchema>;

/** The `201` of `POST /api/npc-series`: the series as it stands, and the NPC the launch skipped (H24). */
export const npcSeriesLaunchedSchema = npcSeriesDetailSchema
  .extend({
    skipped: z.array(z.object({ npcId: z.string().min(1), name: z.string().min(1) }).strict()),
  })
  .strict();
export type NpcSeriesLaunched = z.infer<typeof npcSeriesLaunchedSchema>;

/* ------------------------------------------------ the action of the player */

/**
 * What the player of the SERVER hands back at each move (§7.1, H9): a model
 * output, validated here. STRICT - a key the model invented is a refusal that
 * goes back to it, not a field ignored - and one of four:
 *
 * - `play-page` - the advance of the person (`rejeu.md` §7): the page, the
 *   values of its fields keyed by block, the button pressed (null for the page's
 *   own duration), the card taken on a page of choice;
 * - `reply` - the person's answer in the conversation (`rejeu.md` §8);
 * - `wait` - nothing: the interviewer writes, or the cards are being prepared;
 * - `leave` - the person stops answering (H10).
 *
 * `values` and `pressedBlockId` default, so a model that has nothing to put in a
 * page says less; the bounds are the public route's own, because the server
 * plays the action through the same functions.
 */
export const npcPlayerActionSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('play-page'),
      pageId: z.string().min(1),
      values: sessionValuesSchema.default({}),
      pressedBlockId: z.string().min(1).nullable().default(null),
      cardId: noyauCardIdSchema.optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('reply'),
      text: z.string().trim().min(1).max(SESSION_MESSAGE_MAX_CHARS),
    })
    .strict(),
  z.object({ action: z.literal('wait') }).strict(),
  z.object({ action: z.literal('leave') }).strict(),
]);
export type NpcPlayerAction = z.infer<typeof npcPlayerActionSchema>;

/**
 * How one call of the player ended (`session_player_calls`, §7.4): `played` - the
 * action was valid and the engine took it; `refused` - valid, and refused by the
 * engine, whose phrase went back to the player; `malformed` - the output did not
 * parse (asked again once, then the replay is blocked).
 */
export const npcPlayerCallStatusSchema = z.enum(['played', 'refused', 'malformed']);
export type NpcPlayerCallStatus = z.infer<typeof npcPlayerCallStatusSchema>;

/**
 * One call of the player as the session records it (§7.4), at the régime of
 * `turnCallSchema`: the model, the four buckets of tokens, the cost at the seat's
 * tariff, the action rendered - null when it was malformed - and the detail of a
 * refusal or of a parse problem. Counted APART from the device (H15).
 */
export const npcPlayerCallSchema = z.object({
  startedAt: z.string().datetime(),
  model: z.string().min(1).nullable().default(null),
  inputTokens: z.number().int().nonnegative().default(0),
  outputTokens: z.number().int().nonnegative().default(0),
  cacheWriteTokens: z.number().int().nonnegative().default(0),
  cacheReadTokens: z.number().int().nonnegative().default(0),
  costEur: z.number().nonnegative().default(0),
  durationMs: z.number().int().nonnegative().default(0),
  status: npcPlayerCallStatusSchema,
  action: npcPlayerActionSchema.nullable().default(null),
  detail: z.string().max(NPC_REASON_MAX).nullable().default(null),
});
export type NpcPlayerCall = z.infer<typeof npcPlayerCallSchema>;

/* ------------------------------------------------------------- « Regarder » */

/**
 * The header of the thin dark bar of « Regarder » (§11, M5): the NPC in link,
 * the experience and its version, the state, the turn. Nothing of the machine.
 * `npc` is null once the NPC was deleted for good.
 */
export const npcWatchHeaderSchema = z
  .object({
    replayId: z.string().min(1),
    npc: z
      .object({
        id: z.string().min(1),
        name: z.string().min(1),
        versionNumber: z.number().int().positive().nullable(),
      })
      .strict()
      .nullable(),
    experienceName: z.string().min(1),
    experienceVersionNumber: z.number().int().positive().nullable(),
    player: replayPlayerSchema,
    status: sessionStatusSchema,
    outcome: npcRunOutcomeSchema.nullable(),
    turn: z.number().int().nonnegative(),
  })
  .strict();
export type NpcWatchHeader = z.infer<typeof npcWatchHeaderSchema>;

/**
 * `GET /api/replays/{id}/watch` (§11, H17): the person's screen of a replay, read
 * again every two seconds. `live` is false once the replay is over, when the bar
 * offers « Revoir en différé ». The screen itself is the VIEW the person's own
 * route serves - the experience with its theme, the values, the conversation -
 * and nothing the engine knows (`rejeu.md` D11).
 *
 */
export const NPC_WATCH_POLL_MS = 2000;
export const npcWatchSchema = z
  .object({
    header: npcWatchHeaderSchema,
    live: z.boolean(),
    screen: sessionViewSchema,
  })
  .strict();
export type NpcWatch = z.infer<typeof npcWatchSchema>;

/** The speeds of the playback (§11). */
export const NPC_PLAYBACK_SPEEDS = [1, 2, 4] as const;
export type NpcPlaybackSpeed = (typeof NPC_PLAYBACK_SPEEDS)[number];

/**
 * One step of the recorded sequence of a finished replay (§11): what the person
 * saw, in the order it happened, each with its instant and its offset from the
 * start in milliseconds - what the player replays at its own speed, without
 * waiting for the interviewer in front of a client.
 *
 * - `page` - a page was entered (the bar of progress is marked at each);
 * - `temps` - a temps of the page opened;
 * - `leave` - the page was left: the values given, the button pressed, the card
 *   taken;
 * - `message` - a bubble of the conversation, the interviewer's or the person's.
 */
export const npcPlaybackStepSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('page'),
      at: z.string().datetime(),
      offsetMs: z.number().int().nonnegative(),
      pageId: z.string().min(1),
      name: z.string(),
    })
    .strict(),
  z
    .object({
      type: z.literal('temps'),
      at: z.string().datetime(),
      offsetMs: z.number().int().nonnegative(),
      pageId: z.string().min(1),
      index: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      type: z.literal('leave'),
      at: z.string().datetime(),
      offsetMs: z.number().int().nonnegative(),
      pageId: z.string().min(1),
      values: sessionValuesSchema,
      pressedBlockId: z.string().min(1).nullable(),
      cardId: noyauCardIdSchema.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('message'),
      at: z.string().datetime(),
      offsetMs: z.number().int().nonnegative(),
      role: z.enum(['interviewer', 'person']),
      text: z.string(),
      kind: z.enum(['turn', 'bubble', 'click']),
      pageId: z.string().min(1).nullable(),
    })
    .strict(),
]);
export type NpcPlaybackStep = z.infer<typeof npcPlaybackStepSchema>;

/**
 * `GET /api/replays/{id}/playback` (§11): the header, the screen the replay ends
 * on (the experience entire, drawn in its theme, which the steps are applied
 * to) and the recorded steps, ordered by `offsetMs`. Answers a refusal while the
 * replay still runs: the live view is for that.
 */
export const npcPlaybackSchema = z
  .object({
    header: npcWatchHeaderSchema,
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    screen: sessionViewSchema,
    steps: z.array(npcPlaybackStepSchema),
  })
  .strict();
export type NpcPlayback = z.infer<typeof npcPlaybackSchema>;

/* ------------------------------------------------------------- the refusals */

/**
 * The refusals of the NPC, the benches and the series are `npcRefusalCodeSchema`
 * of `replay.ts`, beside the replay's own: `POST /api/replays` answers them too
 * (`start_replay { npcId, seriesId }`), and the arrow of imports goes from here to
 * there. The body below takes the three families a route of the NPC can answer:
 * the NPC's, the replay's (`no-protocol-page`...) and the person's
 * (`session-over`, `turn-in-flight`...), at the régime of `replayRefusalSchema`.
 */

/**
 * The body of a refusal of the NPC's routes: the code, and `reason` on
 * `prefill-refused` (why the session cannot give an NPC), `name` on `name-taken`
 * (the other row's name, so the screen names it).
 */
export const npcRefusalSchema = z
  .object({
    error: z.string().min(1),
    code: z.union([npcRefusalCodeSchema, replayRefusalCodeSchema, sessionRefusalCodeSchema]),
    reason: npcPrefillReasonSchema.optional(),
    name: z.string().optional(),
  })
  .strict();
export type NpcRefusal = z.infer<typeof npcRefusalSchema>;
